import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import useHttp from '../../../../hook/use-http';
import { API_URL_MAIN } from '../../../../constants/driveglobalfilterinitalstates';
import styles from './GrowthTrackingTab.module.css';
import moment from 'moment';

const EnergyEfficiencyTrends = ({ filterdata }) => {
  const { sendReq: fetchData } = useHttp();
  const [chartView, setChartView] = useState('Monthly Graph');
  const [data, setData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(moment().subtract(1, 'days'));

  const getDateRange = (monthMoment) => {
    return {
      startDate: monthMoment.clone().startOf('month').format('YYYY-MM-DD'),
      endDate: monthMoment.clone().endOf('month').format('YYYY-MM-DD'),
    };
  };

  const getCumulativeRange = () => {
    const today = moment().subtract(1, 'days');
    const sixMonthsAgo = today.clone().subtract(5, 'months').startOf('month');
    return {
      startDate: sixMonthsAgo.format('YYYY-MM-DD'),
      endDate: today.clone().endOf('month').format('YYYY-MM-DD'),
    };
  };

  const fetchEnergyData = () => {
    // Don't fetch if required filters are missing
    if (!filterdata || Object.keys(filterdata).length === 0) return;
    
    // Check if CITY and DEPO are selected (required filters)
    // if (!filterdata.CITY?.length || !filterdata.DEPO?.length) {
    //   console.log('City and Depo must be selected');
    //   setData([]);
    //   return;
    // }

    let dateRange = {};
    let apiURL = '';

    if (chartView === 'Cumulative') {
      dateRange = getCumulativeRange();
      apiURL = API_URL_MAIN + 'Ace_Growth_tracking/energy_efficiency_monthwise';
    } else if (chartView === 'Monthly Graph') {
      dateRange = getDateRange(selectedMonth);
      apiURL = API_URL_MAIN + 'Ace_Growth_tracking/energy_efficiency_monthlygraph';
    }

    // Build the complete filter object
    const requestBody = {
      filterdata: {
        CITY: filterdata.CITY || [],
        DEPO: filterdata.DEPO || [],
        ROUTE: filterdata.ROUTE || [],
        VIN_NUM: filterdata.VIN_NUM || [],
        REG_NUM: filterdata.REG_NUM || [],
        ...dateRange
      }
    };

    console.log('API Request:', apiURL, requestBody);

    fetchData({ url: apiURL, body: requestBody }, (result) => {
  if (result) {
    if (chartView === 'Cumulative') {
      if (Array.isArray(result)) {
        const transformed = result.map(item => ({
          month: moment(item.key.eventdate).format('MMMM YYYY'),
          targetEnergyEfficiency: parseFloat(item.Target_Energy_Efficiency?.value?.toFixed(3) || 0),
          actualEnergyEfficiency: parseFloat(item.actual_Energy_Efficiency?.value?.toFixed(3) || 0),
        }));
        setData(transformed);
      } else {
        setData([]);
      }
    } else if (chartView === 'Monthly Graph') {
      // CHANGED: Check if result is directly an array (not nested in groupby.buckets)
      if (Array.isArray(result)) {
        // Sort by eventdate
        const sortedData = result.sort(
          (a, b) => new Date(a.key.eventdate) - new Date(b.key.eventdate)
        );

        const transformed = sortedData.map(item => ({
          month: moment(item.key.eventdate).format('DD MMM'),
          targetEnergyEfficiency: parseFloat(item.Target_Energy_Efficiency?.value?.toFixed(3) || 0),
          actualEnergyEfficiency: parseFloat(item.actual_Energy_Efficiency?.value?.toFixed(3) || 0)
        }));

        setData(transformed);
      } else if (result.groupby && Array.isArray(result.groupby.buckets)) {
        // Keep this as fallback if the API sometimes returns the old structure
        const sortedBuckets = result.groupby.buckets.sort(
          (a, b) => new Date(a.key.eventdate) - new Date(b.key.eventdate)
        );

        const transformed = sortedBuckets.map(bucket => ({
          month: moment(bucket.key.eventdate).format('DD MMM'),
          targetEnergyEfficiency: parseFloat(bucket.Target_Energy_Efficiency?.value?.toFixed(3) || 0),
          actualEnergyEfficiency: parseFloat(bucket.actual_Energy_Efficiency?.value?.toFixed(3) || 0)
        }));

        setData(transformed);
      } else {
        setData([]);
      }
    }
  } else {
    setData([]);
  }
});
  };

  // Fetch when dependencies change
  useEffect(() => {
    fetchEnergyData();
  }, [chartView, filterdata, selectedMonth]);

  const goToPreviousMonth = () => {
    setSelectedMonth(prev => prev.clone().subtract(1, 'month'));
  };

  const goToNextMonth = () => {
    setSelectedMonth(prev => prev.clone().add(1, 'month'));
  };

  return (
    <div className={`${styles.dashboardContainer} p-3`}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className={styles.sectionTitle}>Energy Efficiency Trends</h3>

        {/* Only show month selectors for Monthly Graph */}
        {chartView === 'Monthly Graph' && (
          <div className="d-flex align-items-center">
            <span onClick={goToPreviousMonth} className={`${styles.month}`} style={{ cursor: 'pointer' }}>
              <i className="fas fa-chevron-left"></i>
            </span>
            <div className="border p-2 mx-2 rounded">
              {selectedMonth.format('MMMM YYYY')}
            </div>
            <span onClick={goToNextMonth} className={`${styles.month}`} style={{ cursor: 'pointer' }}>
              <i className="fas fa-chevron-right"></i>
            </span>
          </div>
        )}

        <div className="d-flex" style={{ backgroundColor: '#f8f9fa', borderRadius: '25px', padding: '4px' }}>
          <button
            className={`${styles.btnToggle} ${chartView === 'Cumulative' ? styles.active : ''} me-1`}
            onClick={() => setChartView('Cumulative')}
            style={{
              backgroundColor: chartView === 'Cumulative' ? '#358696' : 'transparent',
              color: chartView === 'Cumulative' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '20px',
              padding: '8px 20px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Cumulative
          </button>
          <button
            className={`${styles.btnToggle} ${chartView === 'Monthly Graph' ? styles.active : ''}`}
            onClick={() => setChartView('Monthly Graph')}
            style={{
              backgroundColor: chartView === 'Monthly Graph' ? '#358696' : 'transparent',
              color: chartView === 'Monthly Graph' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '20px',
              padding: '8px 20px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Monthly Graph
          </button>
        </div>
      </div>

      <div className={`${styles.chartContainer} p-3`}>
        {data.length === 0 ? (
          <div className="text-center text-muted p-5">
    No data available for the selected filters
  </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#666' }}
                interval={0}
                label={
                  chartView === "Cumulative"
                    ? {
                      value: 'Monthwise',
                      position: 'insideBottom',
                      offset: -15,
                      style: {
                        textAnchor: 'middle',
                        fill: '#666',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }
                    }
                    : {
                      value: 'Day-Wise',
                      position: 'insideBottom',
                      offset: -15,
                      style: {
                        textAnchor: 'middle',
                        fill: '#666',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }
                    }
                }
              />
              <YAxis
                domain={['auto', 'auto']}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#666' }}
                label={{
                  value: 'Energy Efficiency (Wh/km)',
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle', fill: '#666', fontSize: '12px' }
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}
                formatter={(value, name) => [`${value} Wh/km`, name]}
                labelFormatter={(label) => label}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }} 
                iconType="line" 
                align="right" 
                verticalAlign="top" 
                layout="vertical" 
              />
              <Line 
                type="monotone" 
                dataKey="targetEnergyEfficiency" 
                stroke="#17becf" 
                strokeWidth={2}
                dot={{ fill: '#17becf', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, stroke: '#17becf', strokeWidth: 2, fill: 'white' }}
                name="Benchmark"
              />
              <Line 
                type="monotone" 
                dataKey="actualEnergyEfficiency" 
                stroke="#e377c2" 
                strokeWidth={2}
                dot={{ fill: '#e377c2', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, stroke: '#e377c2', strokeWidth: 2, fill: 'white' }}
                name="Energy Efficiency"
              />

              {chartView === "Cumulative" && (
                <text
                  x="50%"
                  y="95%"
                  textAnchor="middle"
                  fontSize="11"
                  fill="#6c757d"
                  fontStyle="italic"
                >
                  Note: Cumulative will be for 6 months of data.
                </text>
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default EnergyEfficiencyTrends;